import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, ViewContainerRef } from '@angular/core';
import { environment } from 'src/environments/environment';
import { AuthAction, AuthService } from '../../auth/auth.service';
import { MyDashboard } from './model/my-dashboard';
import { WidgetWrapper } from './model/widget-wrapper';
import { PendingReleaseComponent } from './views/pending-release/pending-release.component';
import { QuickAccessComponent } from './views/quick-access/quick-access.component';
import { RecentlyOpenedComponent } from './views/recently-opened/recently-opened.component';
import { WorkInProgressComponent } from './views/work-in-progress/work-in-progress.component';
import { MessageService } from 'primeng/api';
import { LoadWheelService } from '../load-wheel/load-wheel.service';
import { TranslateService } from '@ngx-translate/core';
import { MessageHandlerService } from '../messages/message-handler/message-handler.service';
import { MessageEvent, MessageResponse, MessageType } from '../messages/message-handler/message-handler.component';
import { SpinnerProcess } from 'src/app/core/models/spinner-process';
import { SessionAttribute, SessionService } from 'src/app/core/services/session.service';
import { Observable, last, concat, forkJoin, map, tap } from 'rxjs';
import { MainDashboardComponent } from 'src/app/main-dashboard/main-dashboard.component';
import { Widget } from './model/widget';
import { WidgetComponent } from './widget.component';
import { WidgetType } from 'src/app/core/models/constants';
import { EntityName } from 'src/app/core/models/enumerations/entity-name';
import { LockSameUserMessageService } from 'src/app/core/services/lock-same-user-message.service';


@Injectable({ providedIn: 'root' })
export class WidgetService {

    private myDashboard: MyDashboard | null = null;
    loginChange?: PushSubscription;
    widgetFullScreen!: boolean;
    
    private readonly allEntities: Array<EntityName> = Widget.getEntitySettings().map(setting => setting.id);
    accessibleEntities: Array<EntityName> = [];

    constructor(
        private readonly http: HttpClient,
        private readonly authService: AuthService,
        private readonly session: SessionService,
        private readonly loadWheelService: LoadWheelService,
        private readonly messageService: MessageService,
        private readonly messageHandlerService: MessageHandlerService,
        private readonly lockSameUserMessageService: LockSameUserMessageService,
        private readonly translate: TranslateService) {
        this.authService.loginChange$.subscribe((action: AuthAction) => {
            if (action === AuthAction.LOGOUT) {
                this.myDashboard = null
            }
        });
        this.session.attributeChange$.subscribe((csa) => {
            if(csa?.name === SessionAttribute.ServerAuthToken && !csa.newValue && csa.otherWindow) {
                this.myDashboard = null
            }
        });
    }
    
    private loadAccessibleEntities(): Observable<void> {
        return forkJoin(this.allEntities.map(e => this.authService.hasUserScreen(e)))
        .pipe(
            map(results => this.allEntities.filter((_, index) => results[index])),
            tap(entities => {
                this.accessibleEntities = entities;
            }),
            map((): void => undefined)         
        );
    }
    
    public getAccessibleEntities(): Array<EntityName> {
        return this.accessibleEntities;  
    }
    
    public isAccessible(entity: EntityName): boolean {
        return this.getAccessibleEntities().includes(entity);
    }

    public initDashboard(dashboard: MainDashboardComponent) {
        if (!this.myDashboard?.widgets || this.myDashboard.widgets.length === 0) {
            this.myDashboard = { widgets: [], wizardSetting: Widget.getDefaultWizardSetting()};
            let wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("mainDashboard.loading"), dashboard.viewContainerRef, dashboard.dashMainSection.nativeElement);
            
            // The concat/pipe/last trickery here is because we need the accessible entities compued before we load the widgets, but we only want to run the next/error once
            concat(
               this.loadAccessibleEntities(),
               this.loadWidgets(dashboard)
            ).pipe(last()).subscribe({
               next: () => {
                   this.loadWheelService.hideWheel(wheel);
               },
               error: (error: any) => {
                   this.loadWheelService.hideWheel(wheel);
                   throw new Error(error);
               }
            });
        }
    }

    private loadWidgets(dashboard: MainDashboardComponent): Observable<void> {
        return this.widgetsByPersonId().pipe(
            tap((widgets: Array<Widget>) => {
            this.myDashboard!.widgets = widgets.filter(widget => widget.widgetType !== WidgetType.Wizard);
            let wizardSetting = widgets.find(widget => widget.widgetType === WidgetType.Wizard);
            if(wizardSetting){
                this.myDashboard!.wizardSetting = wizardSetting;
            }
            dashboard.myDashboard = this.myDashboard!;
            }),
            map((): void => undefined)
        );
    }
    
    private widgetsByPersonId(): Observable<Widget[]>{
        return this.http.get<Widget>(`${environment.apiUrl}widget/widgetsByPersonId`)
        .pipe(
            map((data: any) => Widget.BuildWidget(data.response))
        );
    }

    public getWidgetList(): Widget[] {
        return this.myDashboard!.widgets;
    }

    public addWidget(widget: Widget){
        const widgets = [
            ...this.myDashboard!.widgets, widget
        ];
        this.saveWidgetsInternal(widgets, () => this.myDashboard!.widgets.push(widget));
    }

    public askDeleteWidget(widget: Widget, viewContainerRef: ViewContainerRef | null): void {
        this.messageHandlerService.show(this.translate.instant("widgets.common.removeMessage") + "\n" + this.translate.instant("widgets.common.addAgain")
            , MessageType.WIDGET_REMOVE_CONFIRM, (resp: MessageResponse): void => {
                if (resp.event === MessageEvent.OK) {
                    let wheel: SpinnerProcess = this.loadWheelService.showWheel(
                        this.translate.instant("widgets.widgetWizard.deletingWidget"), viewContainerRef);
                    this.deleteWidget(widget.widgetType).subscribe({
                        next: () => {
                            this.myDashboard!.widgets.splice(this.myDashboard!.widgets
                                .findIndex(item => item.widgetType === widget.widgetType), 1);
                            this.messageService.add({
                                severity: 'info', summary: 'Widget Deleted Successfully',
                                detail: 'your widget has been deleted.', life: 10000
                            });
                            this.loadWheelService.hideWheel(wheel);
                        },
                        error: (error: any) => {
                            this.loadWheelService.hideWheel(wheel);
                            throw new Error(error);
                        }
                    });
                }
            });
    }

    private deleteWidget(widgetType: string): Observable<any> {
        return this.http.post<any>(`${environment.apiUrl}widget/deleteWidget`, widgetType);
    }

    public saveWidget(widgetComponent:any, settings: any, widgetWrapper: WidgetWrapper){
        widgetWrapper.widgetData.entitiesFilter = settings.entityType ? settings.entityType : [];
        widgetWrapper.widgetData.itemCount = settings.itemCount;
        widgetWrapper.widgetData.otherOptions = widgetWrapper.widgetData.getSerializedOption();
        this.saveWidgetProperties(widgetWrapper.widgetData, () => widgetComponent.updateData());
    }

    public saveWidgetProperties(widget: Widget, doAfter: CallableFunction | null = null): void {
        this.updateWidgetCurrentValues(widget);
        this.updateWidgetByName(widget.widgetType, 
            widget.posVertical,
            widget.posHorizonal, widget.visibility, 
            widget.itemCount,
            widget.getSerializedOption()).subscribe({
            next: () => {
                if(doAfter){
                    doAfter();
                }
            },
            error: (error: any) => {
                throw new Error(error);
            }
        });
    }

    public saveWidgets(doAfter: CallableFunction | null = null): void {
        this.saveWidgetsInternal(this.myDashboard!.widgets, doAfter);
    }

    public saveWidgetsInternal(widgets: Widget[] , doAfter: CallableFunction | null = null): void {
        widgets.forEach(widget => {
            this.updateWidgetCurrentValues(widget);
            widget.otherOptions = widget.getSerializedOption();
        })
        this.updateWidgets(widgets).subscribe({
            next: () => {
                if(doAfter){
                    doAfter();
                }
            },
            error: (error: any) => {
                throw new Error(error);
            }
        });
    }

    private updateWidgetCurrentValues(widget: Widget){
        if(widget.gridItem){
            widget.posVertical = widget.gridItem.y;
            widget.posHorizonal = widget.gridItem.x;
        }
    }

    private updateWidgets(widgets: Array<Widget>): Observable<any>{
        return this.http.post<any>(`${environment.apiUrl}widget/updateWidgets`, widgets);
    }

    private updateWidgetByName(widgetType: string, posVertical: number, posHorizonal: number, 
                    visibility: string, itemCount: number, otherOptions: string): Observable<any> {
        const data = {"widgetType": widgetType,
                        "posVertical": posVertical,
                        "posHorizonal": posHorizonal,
                        "visibility": visibility,
                        "itemCount": itemCount,
                        "otherOptions": otherOptions
        };
        return this.http.post<any>(`${environment.apiUrl}widget/updateWidgetByName`, data);
    }


    public loadComponent(widgetComponent: WidgetComponent): void{
        let component: any;
        if (widgetComponent.widgetWrapper.widgetData.widgetType ===  WidgetType.PendingRelease) {
            component = PendingReleaseComponent;
        } else if (widgetComponent.widgetWrapper.widgetData.widgetType === WidgetType.QuickAccess) {
            component = QuickAccessComponent;
        }
        else if (widgetComponent.widgetWrapper.widgetData.widgetType === WidgetType.WorkInProgress) {
            component = WorkInProgressComponent;
        }
        else if (widgetComponent.widgetWrapper.widgetData.widgetType === WidgetType.RecentlyOpened) {
            component = RecentlyOpenedComponent;
        }
        widgetComponent.widgetContent.viewContainerRef.clear();
        let componentRef: any = widgetComponent.widgetContent.viewContainerRef.createComponent(component);
        componentRef.instance.widgetWrapper = widgetComponent.widgetWrapper;
        widgetComponent.childInstance = componentRef.instance;
    }



    public getRows(widgetComponent: any, widgetData: Widget){
        let wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("widgets.common.loading"), 
            widgetComponent.viewContainerRef,
            widgetComponent.widgetDataContainer.nativeElement.parentNode.parentNode);
            
        const entitiesFilter = widgetData.entitiesFilter?.length > 0 ? widgetData.entitiesFilter : this.allEntities;
        const entityFilterString = entitiesFilter.join(',');
        if(widgetData.widgetType === WidgetType.PendingRelease){
            this.getPendingRelease(widgetData.itemCount, entityFilterString).subscribe({
                next: (data: any) => {                    
                    widgetComponent.rows = data.response;
                    this.setOfflineEventName(widgetComponent.rows);
                    this.loadWheelService.hideWheel(wheel);
                },
                error: (error: any) => {
                    this.loadWheelService.hideWheel(wheel);
                    throw new Error(error);
                }
            });
        } else if (widgetData.widgetType === WidgetType.WorkInProgress) {
            this.getWorkInProgress(widgetData.itemCount, entityFilterString).subscribe({
                next: (data: any) => {
                    widgetComponent.rows = data.response;
                    this.setOfflineEventName(widgetComponent.rows);
                    this.loadWheelService.hideWheel(wheel);
                },
                error: (error: any) => {
                    this.loadWheelService.hideWheel(wheel);
                    throw new Error(error);
                }
            });
        }
    }

    private setOfflineEventName(rows: any){
        rows.forEach((row: any) => {
            if(row.ENTITY_TYPE === EntityName.OFFLINE_EVENT){
                if(row.COUNT_ASSETS > 1){
                    row.ENTITY_NAME = this.translate.instant(Number(row.COUNT_ASSETS) === 2 ? 'widgets.common.moreAsset' : 'widgets.common.moreAssets', {entityName: row.ENTITY_NAME, "count": row.COUNT_ASSETS-1});
                } else if(row.COUNT_UNITS > 1) {
                    row.ENTITY_NAME = this.translate.instant(Number(row.COUNT_UNITS) === 2 ? 'widgets.common.moreUnit' : 'widgets.common.moreUnits', {entityName: row.ENTITY_NAME, "count": row.COUNT_UNITS-1});
                }
            }
        });

    }

    private getPendingRelease(itemCount: number, entitiesFilter: string): Observable<any> {
        const params = new HttpParams()
            .append('itemCount', itemCount)
            .append('listEntities', entitiesFilter);
        return this.http.get<any>(`${environment.apiUrl}widget/getPendingReleaseRows`, {params});
    }

    private getWorkInProgress(itemCount: number, entitiesFilter: string): Observable<any> {
        const params = new HttpParams()
            .append('itemCount', itemCount)
            .append('listEntities', entitiesFilter);
        return this.http.get<any>(`${environment.apiUrl}widget/getWorkInProgressRows`, {params});
    }

    public getMyDashboard(): MyDashboard {
        this.myDashboard ??= { widgets: [], wizardSetting: Widget.getDefaultWizardSetting()};
        return this.myDashboard;
    }

    public setMyDashboard(myDashboard: MyDashboard): void {
        this.myDashboard = myDashboard;
    }
    
    public getEntityTooltip(row: any): string {
        const type: EntityName = row.ENTITY_TYPE ?? row.entityType;
      
        let tip = `${type} ID: ${row.ENTITY_ID ?? row.entityId}`;
        if(!this.isAccessible(type))
        {
           tip += `\n\n(${type} screen access unavailable)`;
        }
        
        return tip;
    }
    
    public getEntityNgClass(entityName: EntityName): Array<string> {
        return this.isAccessible(entityName) ? ['cursor-pointer','text-blue-600'] : ['cursor-not-allowed','text-blue-900'];
    }
    

    private goTo(path: string, entityID: string, entityName: EntityName): void {
        if(this.isAccessible(entityName))
        {
            const target: string = entityName + entityID;
            this.lockSameUserMessageService.validateEntityIsLockedByUserAndOpenEntityTab(entityName, entityID, location.pathname + `#/ED/${path}/${entityID}`, `${target}`);
        }  
    }

    public goToCompany(companyID: string) {
        this.goTo('company', companyID, EntityName.COMPANY);
    }

    public goToSite(assetID: string) {
        this.goTo('site', assetID, EntityName.SITE);
    }

    public goToPipeline(assetID: string) {
        this.goTo('pipeline', assetID, EntityName.ASSET_PIPELINE);
    }

    public goToTLine(assetID: string) {
        this.goTo('transmissionLine', assetID, EntityName.ASSET_TRANSMISSION_LINE);
    }

    public goToPlant(plantID: string) {
        this.goTo('plant', plantID, EntityName.PLANT);
    }

    public goToArea(areaID: string) {
        this.goTo('area', areaID, EntityName.AREA);
    }

    public goToUnit(unitID: string) {
        this.goTo('unit', unitID, EntityName.UNIT);
    }

    public goToTank(tankID: string) {
        this.goTo('tank', tankID, EntityName.TANK);
    }

    public goToProject(projectID: string, projectType: EntityName) {
        this.goTo(projectType === EntityName.PROJECT_CONFIRMED  ? 'confirmedProject' : 'unconfirmedProject', projectID, projectType);
    }

    public goToDrive(driveID: string) {
        this.goTo('drive', driveID, EntityName.TURBINE);
    }

    public goToEquipment(equipmentID: string) {
        this.goTo('otherEquipment', equipmentID, EntityName.OEQUIPMENT);
    }

    public goToMEquipment(mEquipmentID: string) {
        this.goTo('miningEquipment', mEquipmentID, EntityName.MEQUIPMENT);
    }
        
    public goToBoiler(entityID: string) {
        this.goTo('boiler', entityID, EntityName.BOILER);
    }

    public goToOfflineEvent(entityID: string) {
        this.goTo('offlineEvent', entityID, EntityName.OFFLINE_EVENT);
    }

    public goToPlantLtsa(entityID: string) {
        this.goTo('plantLtsa', entityID, EntityName.PLANT_LTSA);
    }

    public goToUnitLtsa(entityID: string) {
        this.goTo('unitLtsa', entityID, EntityName.UNIT_LTSA);
    }

    public goToPipelineLtsa(entityID: string) {
        this.goTo('pipelineLtsa', entityID, EntityName.PIPELINE_LTSA);
    }
}
